import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { API } from "../api";

import {
  FiEdit,
  FiTrash2,
  FiEye,
  FiSearch,
  FiClipboard,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";


const PER = 10;

export default function CouponCode() {
  const navigate = useNavigate();

  // States
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterModel, setFilterModel] = useState(0);
  const [carModels, setCarModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewCoupon, setViewCoupon] = useState(null);
  const [deleteCoupon, setDeleteCoupon] = useState(null);

  // Load car models
  const loadCarModels = useCallback(async () => {
    try {
      const response = await API.get("/cars", {
        params: {
          page: 1,
          limit: 1000,
          search: "",
          selectedcartype: 0,
        },
      });

      const models = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setCarModels(models);
    } catch (error) {
      console.error("LOAD CAR MODELS ERROR:", error);

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load car models"
      );
    }
  }, []);

  // Load coupons
  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);

      const response = await API.get("/coupons", {
        params: {
          page,
          limit: PER,
          search,
          selectedcartype: filterModel,
        },
      });

      const result = response.data || {};

      setData(Array.isArray(result.data) ? result.data : []);
      setTotal(Number(result.total) || 0);
      setTotalPages(Number(result.totalPages) || 1);
    } catch (error) {
      console.error("LOAD COUPONS ERROR:", error);

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load coupons"
      );

      setData([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterModel]);

  // Initial load
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }

    loadCarModels();
  }, [navigate, loadCarModels]);

  // Load coupons
  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  // Search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Model filter
  const handleModelFilter = (e) => {
    setFilterModel(e.target.value);
    setPage(1);
  };

  // Add
  const handleAddCoupon = () => {
    navigate("/coupon-code/add");
  };

  // Edit
  const handleEditCoupon = (row) => {
    navigate("/coupon-code/add", {
      state: {
        couponId: row.code_id,
      },
    });
  };

  // View
  const handleViewCoupon = (row) => {
    setViewCoupon(row);
  };

  // Open delete
  const openDelete = (row) => {
    setDeleteCoupon(row);
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteCoupon) {
      return;
    }

    try {
      setSaving(true);

      await API.delete(`/coupons/${deleteCoupon.code_id}`);

      toasts.success("Coupon deleted successfully");

      setDeleteCoupon(null);

      await loadCoupons();
    } catch (error) {
      console.error("DELETE COUPON ERROR:", error);

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to delete coupon"
      );
    } finally {
      setSaving(false);
    }
  };

  // Status
  const handleStatusToggle = async (row) => {
    const currentStatus = Number(row.code_active) === 1 ? 1 : 0;
    const newStatus = currentStatus === 1 ? 0 : 1;

    try {
      setSaving(true);

      await API.put(`/coupons/status/${row.code_id}`, {
        status: newStatus,
      });

      toasts.success(
        newStatus === 1
          ? "Coupon activated successfully"
          : "Coupon deactivated successfully"
      );

      await loadCoupons();
    } catch (error) {
      console.error("STATUS ERROR:", error);

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update coupon status"
      );
    } finally {
      setSaving(false);
    }
  };

  // Get model names
  const getModelNames = (row) => {
    if (row.carmodel_name) {
      return row.carmodel_name;
    }

    if (row.carmodel_id) {
      const ids = String(row.carmodel_id)
        .split(",")
        .map((id) => Number(id.trim()))
        .filter(Boolean);

      const names = ids
        .map((id) => {
          const model = carModels.find(
            (car) => Number(car.carmodel_id) === id
          );

          return model ? model.carmodel_name : null;
        })
        .filter(Boolean);

      return names.length ? names.join(", ") : "—";
    }

    return "—";
  };

  return (
    <Layout activePage="Coupon Code">
      <main className="dashboard-main">
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Coupon Code</span>
              <span className="rec-count ms-1">{total} coupons</span>
            </h3>

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search coupon..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <select
              className="season-input"
              value={filterModel}
              onChange={handleModelFilter}
            >
              <option value={0}>— All Models —</option>

              {carModels.map((model) => (
                <option key={model.carmodel_id} value={model.carmodel_id}>
                  {model.carmodel_name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="um-btn-primary"
              onClick={handleAddCoupon}
            >
              <span className="um-add-icon">＋</span>
              Add Coupon
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="text-center">#</th>
                  <th className="text-center">Coupon Code</th>
                  <th className="text-center">Car Models</th>
                  <th className="text-center">From Date</th>
                  <th className="text-center">To Date</th>
                  <th className="text-center">Discount</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={18} />
                      </div>

                      <div className="empty-title">No coupons found</div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr key={row.code_id} className="um-row">
                      <td className="text-center">
                        {(page - 1) * PER + index + 1}
                      </td>

                      <td className="text-center">
                        <strong>{row.code_name || "—"}</strong>
                      </td>

                      <td className="text-center">
                        {getModelNames(row)}
                      </td>

                      <td className="text-center">
                        {row.from_date
                          ? String(row.from_date).substring(0, 10)
                          : "—"}
                      </td>

                      <td className="text-center">
                        {row.to_date
                          ? String(row.to_date).substring(0, 10)
                          : "—"}
                      </td>

                      <td className="text-center">
                        <strong>{row.discount ?? 0}%</strong>
                      </td>

                      <td className="um-td">
                        <div className="status-wrap">
                          <div
                            className={`status-toggle ${
                              Number(row.code_active) === 1
                                ? "status-active"
                                : "status-inactive"
                            }`}
                            onClick={() =>
                              !saving && handleStatusToggle(row)
                            }
                            role="button"
                            tabIndex={0}
                            aria-label={
                              Number(row.code_active) === 1
                                ? "Deactivate coupon"
                                : "Activate coupon"
                            }
                          >
                            <span
                              className={`status-knob ${
                                Number(row.code_active) === 1
                                  ? "knob-active"
                                  : "knob-inactive"
                              }`}
                            />
                          </div>

                          <span
                            className={`status-text ${
                              Number(row.code_active) === 1
                                ? "text-active"
                                : "text-inactive"
                            }`}
                          >
                            {Number(row.code_active) === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="action-buttons d-flex justify-content-center gap-2">
                          <button
                            type="button"
                            className="action-btn action-view btn btn-sm"
                            title="View"
                            onClick={() => handleViewCoupon(row)}
                          >
                            <FiEye />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit btn btn-sm"
                            title="Edit"
                            onClick={() => handleEditCoupon(row)}
                          >
                            <FiEdit />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete btn btn-sm"
                            title="Delete"
                            onClick={() => openDelete(row)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="pagination">
              <span className="pagin-info">
                {total > 0
                  ? `Showing ${(page - 1) * PER + 1}–${Math.min(
                      page * PER,
                      total
                    )} of ${total}`
                  : "No records"}
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page === 1 || loading}
                  onClick={() =>
                    setPage((previous) => Math.max(1, previous - 1))
                  }
                >
                  ← Prev
                </button>

                <span className="pagin-info">
                  Page {page} / {totalPages || 1}
                </span>

                <button
                  type="button"
                  className="pg-btn"
                  disabled={page >= totalPages || loading}
                  onClick={() =>
                    setPage((previous) =>
                      Math.min(totalPages, previous + 1)
                    )
                  }
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {viewCoupon && (
        <div className="um-overlay" onClick={() => setViewCoupon(null)}>
          <div
            className="um-modal um-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="um-modal-head">
              <h2 className="um-modal-title">Coupon Details</h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={() => setViewCoupon(null)}
              >
                ✕
              </button>
            </div>

            <div className="um-modal-scroll">
              <div className="profile-header">
                <h3 className="profile-name">
                  {viewCoupon.code_name || "Coupon"}
                </h3>
              </div>

              <div className="sec-div">
                <span>BASIC INFORMATION</span>
              </div>

              <div className="row g-3">
                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Coupon Code</div>

                      <div className="profile-row-value">
                        {viewCoupon.code_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">From Date</div>

                      <div className="profile-row-value">
                        {viewCoupon.from_date
                          ? String(viewCoupon.from_date).substring(0, 10)
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">To Date</div>

                      <div className="profile-row-value">
                        {viewCoupon.to_date
                          ? String(viewCoupon.to_date).substring(0, 10)
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Discount</div>

                      <div className="profile-row-value">
                        {viewCoupon.discount ?? 0}%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Status</div>

                      <div className="profile-row-value">
                        {Number(viewCoupon.code_active) === 1
                          ? "Active"
                          : "Inactive"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sec-div">
                <span>APPLICABLE CAR MODELS</span>
              </div>

              <div className="mod-grid">
                {String(viewCoupon.carmodel_id || "")
                  .split(",")
                  .map((id) => id.trim())
                  .filter(Boolean)
                  .map((id) => {
                    const model = carModels.find(
                      (car) => String(car.carmodel_id) === id
                    );

                    return (
                      <div key={id} className="view-mod-chip assigned">
                        <span>✅</span>
                        {model ? model.carmodel_name : `Model ${id}`}
                      </div>
                    );
                  })}

                {!viewCoupon.carmodel_id && (
                  <p className="no-modules-text">
                    No car models assigned
                  </p>
                )}
              </div>
            </div>

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={() => setViewCoupon(null)}
              >
                Close
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={() => handleEditCoupon(viewCoupon)}
              >
                <FiEdit />
                Edit Coupon
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteCoupon && (
        <div
          className="um-overlay"
          onClick={() => !saving && setDeleteCoupon(null)}
        >
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">
              <div className="delete-icon">
                <FiTrash2 />
              </div>

              <h2 className="delete-title">Delete Coupon?</h2>

              <p className="delete-description">
                Are you sure you want to delete{" "}
                <strong>{deleteCoupon.code_name}</strong>?
                <br />
                This action cannot be undone.
              </p>

              <div className="delete-actions">
                <button
                  type="button"
                  className="um-btn-secondary"
                  disabled={saving}
                  onClick={() => setDeleteCoupon(null)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="delete-confirm-btn"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    
    </Layout>
  );
}
